import { Card } from '@trafiflow/shared-ui'
import {
  MessageSquare,
  Send,
  Plus,
  Clock,
  Square,
  Trash2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useChat } from '../../../../hooks/chat'
import { useBrandContext } from '../../hooks/useBrandContext'
import {
  DEFAULT_MARKETING_CHAT_PRESET,
  DEFAULT_FALLBACK_AGENT_ID,
  type MarketingChatPreset,
} from './chatPresets'

interface MarketingAgentsChatProps {
  preset?: MarketingChatPreset
}

export function MarketingAgentsChat({
  preset = DEFAULT_MARKETING_CHAT_PRESET,
}: MarketingAgentsChatProps) {
  const [input, setInput] = useState('')
  const [selectedRoleName, setSelectedRoleName] = useState<string>(
    preset.defaultRoleName
  )
  const agentId = DEFAULT_FALLBACK_AGENT_ID
  const [agentName, setAgentName] = useState<string>(preset.defaultRoleName)
  const agentLoading = false
  const agentError = null

  const { brandContext } = useBrandContext()

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    sessions,
    currentSessionId,
    messages,
    isLoading,
    sendMessage,
    createNewSession,
    deleteSession,
    selectSession,
    stopGeneration,
  } = useChat({ agentId, brandContext, systemPrompt: preset.systemPrompt })

  useEffect(() => {
    setSelectedRoleName(preset.defaultRoleName)
    setAgentName(preset.defaultRoleName)
  }, [preset.defaultRoleName, preset.id])

  useEffect(() => {
    setAgentName(selectedRoleName)
  }, [selectedRoleName])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isLoading || !agentId) return

    const message = input.trim()
    setInput('')
    await sendMessage(message)
    inputRef.current?.focus()
  }

  const handleSuggestionClick = async (text: string) => {
    if (isLoading || !agentId) return
    await sendMessage(text)
  }

  const handleQuickAction = async (prompt: string) => {
    if (isLoading || !agentId) return
    await sendMessage(prompt)
  }

  const hasMessages = messages.length > 0
  const inputDisabled = isLoading || !agentId || agentLoading || !!agentError

  return (
    <div className="flex h-[calc(100vh-64px)]">
      <div className="w-64 border-r border-tf-border bg-tf-bg-muted flex flex-col">
        <div className="p-4 border-b border-tf-border">
          <button
            onClick={createNewSession}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New Chat</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {sessions.length > 0 && (
            <p className="px-3 py-2 text-xs font-semibold text-tf-text-muted uppercase">
              Recent
            </p>
          )}
          {sessions.map((session) => (
            <div
              key={session.id}
              className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${ currentSessionId === session.id ? 'bg-tf-feedback-info-bg' : 'hover:bg-tf-bg-hover' }`}
              onClick={() => selectSession(session.id)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-tf-text truncate">
                  {session.title}
                </p>
                <p className="text-xs text-tf-text-muted flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {session.createdAt.toLocaleDateString()}
                </p>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  deleteSession(session.id)
                }}
                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-tf-feedback-error-bg rounded transition-opacity"
              >
                <Trash2 className="w-3.5 h-3.5 text-tf-feedback-error-text" />
              </button>
            </div>
          ))}
          {sessions.length === 0 && (
            <p className="px-3 py-4 text-sm text-tf-text-muted text-center">
              No conversations yet
            </p>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        <div className="px-6 py-4 border-b border-tf-border">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-tf-text">
                  {selectedRoleName}
                </h1>
                <p className="text-sm text-tf-text-muted">
                  {agentLoading
                    ? 'Resolving agent...'
                    : agentName || 'Select a role to begin'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm text-tf-text-muted">Role</label>
              <select
                value={selectedRoleName}
                onChange={(e) => setSelectedRoleName(e.target.value)}
                className="px-3 py-2 border border-tf-border rounded-lg text-sm bg-tf-bg-card focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {preset.roles.map((roleName) => (
                  <option key={roleName} value={roleName}>
                    {roleName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {agentError && (
            <div className="mt-3 text-sm text-tf-feedback-error-text bg-tf-feedback-error-bg border border-tf-feedback-error-border rounded-lg px-3 py-2">
              {agentError}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto">
            {!hasMessages ? (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-tf-feedback-info-bg rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-2xl font-bold text-tf-text mb-2">
                    {preset.welcomeTitle}
                  </h2>
                  <p className="text-tf-text-muted">{preset.welcomeDescription}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {preset.quickActions.map((action) => (
                    <button
                      key={action.title}
                      onClick={() => handleQuickAction(action.prompt)}
                      className="text-left"
                      disabled={inputDisabled}
                    >
                      <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 border-transparent hover:border-primary/30 h-full">
                        <action.icon
                          className={`w-6 h-6 ${action.colorClass} mb-2`}
                        />
                        <h3 className="font-medium text-tf-text text-sm">
                          {action.title}
                        </h3>
                        <p className="text-xs text-tf-text-muted">{action.desc}</p>
                      </Card>
                    </button>
                  ))}
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-tf-text-muted text-center mb-3">
                    Or try one of these:
                  </p>
                  {preset.suggestions.map((suggestion, index) => {
                    const Icon = suggestion.icon
                    return (
                      <button
                        key={`${preset.id}-suggestion-${index}`}
                        onClick={() => handleSuggestionClick(suggestion.text)}
                        className="w-full flex items-center gap-3 px-4 py-3 bg-tf-bg-muted hover:bg-tf-bg-hover rounded-lg transition-colors text-left"
                        disabled={inputDisabled}
                      >
                        <Icon className="w-5 h-5 text-tf-text-muted" />
                        <span className="text-sm text-tf-text-sub">
                          {suggestion.text}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => {
                  const isThinking = message.role === 'assistant' && !message.content && isLoading
                  return (
                    <div
                      key={message.id}
                      className={`flex ${ message.role === 'user' ? 'justify-end' : 'justify-start' }`}
                    >
                      <div
                        className={`max-w-[80%] px-4 py-3 rounded-2xl ${ message.role === 'user' ? 'bg-primary text-white' : 'bg-tf-bg-hover text-tf-text' }`}
                      >
                        <div className="whitespace-pre-wrap text-sm">
                          {isThinking ? (
                            <span className="flex items-center gap-1 h-5">
                              <span
                                className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce"
                                style={{ animationDelay: '0ms', animationDuration: '1.1s' }}
                              />
                              <span
                                className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce"
                                style={{ animationDelay: '180ms', animationDuration: '1.1s' }}
                              />
                              <span
                                className="w-2 h-2 rounded-full bg-current opacity-60 animate-bounce"
                                style={{ animationDelay: '360ms', animationDuration: '1.1s' }}
                              />
                            </span>
                          ) : (
                            message.content
                          )}
                        </div>
                        {!isThinking && (
                          <div
                            className={`text-xs mt-1 ${ message.role === 'user' ? 'text-white/70' : 'text-tf-text-muted' }`}
                          >
                            {message.createdAt.toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-tf-border">
          <div className="max-w-3xl mx-auto">
            <form onSubmit={handleSubmit} className="flex items-center gap-3">
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={preset.inputPlaceholder}
                  className="w-full px-4 py-3 pr-12 border border-tf-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  disabled={inputDisabled}
                />
                {isLoading ? (
                  <button
                    type="button"
                    onClick={stopGeneration}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-tf-feedback-error-text text-white rounded-lg hover:opacity-90 transition-colors"
                  >
                    <Square className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
                    disabled={inputDisabled || !input.trim()}
                  >
                    <Send className="w-4 h-4" />
                  </button>
                )}
              </div>
            </form>
            <p className="text-xs text-tf-text-muted text-center mt-2">
              {preset.disclaimer}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MarketingAgentsChat
